import React, { memo, useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import emoji from 'node-emoji';
import Alert from 'utils/packages/Alert';
import ScrollView from 'design-system/ScrollView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import { useAppSelector } from 'store/hooks';
import { rem } from 'design-system';
import Links from 'utils/links';
import iconSource from 'design-system/img/notifications.png';
import PaymentToastModal from 'features/pay/components/toast/PaymentToastModal';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { muteAll, useFetchOld, useTimeout } from 'hooks';
import { useSetAccountsSpace } from '../hooks';
import { useSpaceAccounts } from '../hooks/useSpaceAccounts';
import { SelectAccountsBody } from '../components';
import { isWeb } from '../../../constants';
import { selectSpaces } from '../reducers/selectors';
import { sendSpaceReminder } from '../actions';
import useMoveSpaceModal from '../hooks/useMoveSpaceModal';
const SelectAccountsPage = ({ spaceInfo, withoutHeader, onFinish, scrollY, title, subtitle, submitButtonText, onMoveAccounts, }) => {
    const { accounts, onSelected } = useSpaceAccounts(spaceInfo);
    const { isSameTierSpaceMovement, showMoveSpaceUpgradeModal } = useMoveSpaceModal();
    const shouldShowToastRef = React.useRef(false);
    const onFinishWithToast = React.useCallback((space) => {
        onFinish?.(space);
        if (shouldShowToastRef.current) {
            onMoveAccounts?.();
            shouldShowToastRef.current = false;
        }
    }, [onFinish, onMoveAccounts]);
    const [busy, updateSpace] = useSetAccountsSpace(spaceInfo?.space, accounts, onFinishWithToast);
    const updateSpaceWithTierCheck = useCallback(async () => {
        // Check tier movements across all accounts
        let shouldShowUpgradeModal = false;
        let shouldShowToast = false;
        const flattenedMoves = accounts.flatMap(({ space, accounts: spaceAccounts }) => spaceAccounts.map((account) => ({ account, targetSpaceId: space.id })));
        for (const { account, targetSpaceId } of flattenedMoves) {
            const accountSpaceId = 'spaceId' in account ? account.spaceId : undefined;
            if (accountSpaceId !== targetSpaceId) {
                const isSameTier = isSameTierSpaceMovement(accountSpaceId, targetSpaceId);
                if (isSameTier === true && !shouldShowToast) {
                    shouldShowToast = true;
                }
                if (isSameTier === false) {
                    shouldShowUpgradeModal = true;
                    break;
                }
            }
        }
        shouldShowToastRef.current = shouldShowToast;
        if (shouldShowUpgradeModal) {
            showMoveSpaceUpgradeModal(async () => {
                shouldShowToastRef.current = false;
                await updateSpace();
            });
            return;
        }
        await updateSpace();
    }, [
        accounts,
        isSameTierSpaceMovement,
        showMoveSpaceUpgradeModal,
        updateSpace,
    ]);
    const createdSpaces = useAppSelector(selectSpaces);
    const onUpdateSpaceBehindPaywall = useRequiredBenefitCallback(() => updateSpaceWithTierCheck(), 'spaces');
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING);
    const onNext = useCallback(async () => {
        if (busy || !spaceInfo?.space)
            return;
        if (spaceInfo.space.id < 0 &&
            !accounts
                .find(({ space: { id } }) => id === spaceInfo.space.id)
                ?.accounts?.filter(({ id }) => id)?.length) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You need to add at least one account to a space before creating it.');
        }
        else if (createdSpaces.length > 1 && spaceInfo.space.id > 0) {
            updateSpaceWithTierCheck();
        }
        else {
            onUpdateSpaceBehindPaywall();
        }
    }, [
        accounts,
        busy,
        spaceInfo?.space,
        onUpdateSpaceBehindPaywall,
        createdSpaces,
        updateSpaceWithTierCheck,
    ]);
    const onHelpPress = useCallback(() => {
        Links.openKnownLink('spacesHelp');
    }, []);
    const [, , , fetchSendReminder] = useFetchOld(muteAll);
    const [showToast, setShowToast] = useState(false);
    useTimeout(() => setShowToast(false), showToast ? 3000 : undefined);
    const onSendReminder = useCallback(async () => {
        if (!spaceInfo?.space?.id)
            return;
        const res = await fetchSendReminder(sendSpaceReminder(spaceInfo.space.id, 'SPACE_UPGRADE_REMINDER'));
        if (!res)
            return;
        setShowToast(true);
    }, [fetchSendReminder, spaceInfo?.space.id]);
    const owner = useMemo(() => spaceInfo?.users?.find(({ id }) => spaceInfo?.space.createdBy === id), [spaceInfo?.space.createdBy, spaceInfo?.users]);
    const fallbackSubtitle = isWeb
        ? 'Click accounts to move them to and from this space. You can’t remove the accounts added to this space by other members'
        : 'Tap accounts to move them to and from this space. You can’t remove the accounts added to this space by other members';
    return (<View style={styles.content}>
      <PaymentToastModal isVisible={showToast} onCloseAction={() => setShowToast(false)} title="Sent a reminder to upgrade" message={`To ${owner?.firstName || 'owner'}`} iconSource={iconSource} style={styles.toast}/>
      <ScrollView secondary scrollY={scrollY} contentContainerStyle={contentContainerStyles}>
        {!withoutHeader && (<ScreenInfoHeader title={title || 'Add accounts'} subTitle={subtitle || fallbackSubtitle}/>)}
        {withoutHeader ? (<View style={styles.textInfo}>
            <Text TextThin-16 Gray color="text.secondary">
              {fallbackSubtitle}
            </Text>
          </View>) : null}
        <SelectAccountsBody accounts={accounts} onSelected={busy ? undefined : onSelected} spaceInfo={spaceInfo} onSendReminder={onSendReminder}/>
        <InfoCard onPress={onHelpPress}>
          <Text TextThin-14 Secondary>
            Adding an account to a custom space will move it out of the Private
            Space. An account can only belong to one Space at a time.
            {'\n'}
            <Text Primary Text-14>
              Learn more
            </Text>
          </Text>
        </InfoCard>
      </ScrollView>
      <Button brand floating title={submitButtonText || 'Save'} onPress={onNext} isFetching={busy}/>
    </View>);
};
const styles = StyleSheet.create({
    content: {
        flex: 1,
        flexDirection: 'column',
        overflow: 'hidden',
    },
    textInfo: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    toast: {
        marginTop: rem(56),
    },
});
export default memo(SelectAccountsPage);
